import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Loader2, PlayCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PHASES = [
  { phase: 1, name: "Mercado y Competencia", description: "Bloque 1: si la idea es buena, competidores, hueco, amenazas" },
  { phase: 2, name: "Cliente Ideal", description: "Bloque 2: dolores, deseos, objeciones, ficha del cliente" },
  { phase: 3, name: "Marca y Mensaje", description: "Bloque 3: nombre, branding, tono y el mensaje núcleo (hook + promesa)" },
  { phase: 4, name: "Producto y Oferta", description: "Bloques 4-6: producto 1 y 2, precios, oferta, productos futuros" },
  { phase: 5, name: "Operativa y Herramientas", description: "Bloques 7-9: los PERT (en diagrama) y las herramientas del negocio" },
  { phase: 6, name: "Web y Embudos", description: "Bloques 10-11: arquitectura web, landings, emails y embudos (en diagrama)" },
  { phase: 7, name: "SEO, Blog y Prueba Social", description: "Bloques 12-13: SEO, blog, reseñas y prueba social" },
  { phase: 8, name: "Redes y Publicidad", description: "Bloques 14-15: contenido orgánico y publicidad de pago" },
  { phase: 9, name: "Crecimiento, Atención, Finanzas y Legal", description: "Bloques 16-19: vender más, atención, finanzas y legal" },
];

const escapeHtml = (s) => s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
const sanitizeMermaid = (code) => {
  if (!code) return code;
  return code.split('\n').map((line) => {
    if (/^\s*(flowchart|graph|sequenceDiagram|%%|subgraph|end)\b/.test(line)) return line;
    let l = line;
    l = l.replace(/\[(?!")([^\]]*?)\]/g, (_, t) => `["${t.replace(/"/g, "'").trim()}"]`);
    l = l.replace(/\{(?!")([^}]*?)\}/g, (_, t) => `{"${t.replace(/"/g, "'").trim()}"}`);
    l = l.replace(/\|(?!")([^|]*?)\|/g, (_, t) => `|"${t.replace(/"/g, "'").trim()}"|`);
    return l;
  }).join('\n');
};

// Convierte el texto a HTML, pero saca los bloques ```mermaid``` para pintarlos como diagrama.
const renderAnalysis = (text) => {
  if (!text) return '';
  const regex = /```mermaid\s*([\s\S]*?)```/g;
  let html = '';
  let lastIndex = 0;
  let m;
  const fmt = (t) => {
    const out = [];
    t.split('\n').forEach((line) => {
      let l = escapeHtml(line);
      const h3 = l.match(/^\s*###\s+(.*)$/);
      const h2 = l.match(/^\s*##\s+(.*)$/);
      const h1 = l.match(/^\s*#\s+(.*)$/);
      if (h3) { out.push({ t: '<div style="font-size:1.05rem;font-weight:700;margin:10px 0 4px;">' + h3[1] + '</div>', block: true }); return; }
      if (h2) { out.push({ t: '<div style="font-size:1.25rem;font-weight:700;margin:14px 0 6px;">' + h2[1] + '</div>', block: true }); return; }
      if (h1) { out.push({ t: '<div style="font-size:1.5rem;font-weight:700;margin:18px 0 8px;">' + h1[1] + '</div>', block: true }); return; }
      l = l.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      l = l.replace(/^\s*\*+\s*/, '');
      const plain = l.replace(/<[^>]+>/g, '').trim();
      if (plain.length > 0 && plain.length < 80 && /[A-ZÁÉÍÓÚÑ]/.test(plain) && !/[a-záéíóúñ]/.test(plain) && !l.includes('<strong>')) {
        l = '<strong>' + l + '</strong>';
      }
      out.push({ t: l, block: false });
    });
    let s = '';
    for (let i = 0; i < out.length; i++) {
      s += out[i].t;
      if (i < out.length - 1 && !out[i].block && !out[i + 1].block) s += '<br/>';
    }
    return s;
  };
  while ((m = regex.exec(text)) !== null) {
    html += fmt(text.slice(lastIndex, m.index));
    // El contenido del diagrama mantiene los saltos de línea reales (mermaid los necesita).
    html += `<div class="mermaid">${escapeHtml(sanitizeMermaid(m[1].trim()))}</div>`;
    lastIndex = regex.lastIndex;
  }
  html += fmt(text.slice(lastIndex));
  return html;
};

const AuditPage = ({ toast }) => {
  const [reports, setReports] = useState([]);
  const [running, setRunning] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [extraContext, setExtraContext] = useState('');

  useEffect(() => { loadReports(); }, []);

  // Cargar mermaid una sola vez (desde CDN) para pintar los diagramas.
  useEffect(() => {
    if (window.mermaid) return;
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
    s.onload = () => {
      try { window.mermaid.initialize({ startOnLoad: false, theme: 'neutral' }); } catch (e) { /* noop */ }
    };
    document.body.appendChild(s);
  }, []);

  // Cada vez que se abre/cierra un panel, pintar los diagramas que haya dentro.
  useEffect(() => {
    if (!window.mermaid) return;
    const t = setTimeout(() => {
      try {
        const nodes = document.querySelectorAll('.mermaid:not([data-processed="true"])');
        if (nodes.length) window.mermaid.run({ nodes });
      } catch (e) { /* noop */ }
    }, 60);
    return () => clearTimeout(t);
  }, [expanded, reports]);

  const downloadPDF = (title, text) => {
    const win = window.open('', '_blank');
    const body = renderAnalysis(text); // diagramas como <div class="mermaid">, resto formateado
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <script src="https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js"><\/script>
    <style>
      body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #0c0c0c; line-height: 1.7; }
      h1 { color: #0e3f4b; font-size: 1.6rem; margin-bottom: 8px; }
      .meta { color: #729790; font-size: 0.85rem; margin-bottom: 32px; }
      .mermaid { margin: 20px 0; }
      pre { white-space: pre-wrap; font-family: Georgia, serif; font-size: 1rem; }
      @media print { body { margin: 20px; } .mermaid svg { max-width: 100%; } }
    </style></head><body>
    <h1>${title}</h1>
    <div class="meta">Tu Código Base · ${new Date().toLocaleDateString('es-ES', {day:'numeric',month:'long',year:'numeric'})}</div>
    <div>${body}</div>
    <script>
      window.addEventListener('load', function(){
        function doPrint(){ setTimeout(function(){ window.print(); }, 400); }
        if (window.mermaid) {
          try {
            window.mermaid.initialize({ startOnLoad: false, theme: 'neutral' });
            var r = window.mermaid.run();
            if (r && r.then) { r.then(doPrint).catch(doPrint); } else { doPrint(); }
          } catch (e) { doPrint(); }
        } else { setTimeout(doPrint, 1000); }
      });
    <\/script>
    </body></html>`);
    win.document.close();
  };

  const loadReports = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/audit/reports`);
      setReports(r.data || []);
    } catch (e) { /* noop */ }
  };

  const runPhase = async (phase) => {
    setRunning(phase);
    toast({ title: `Ejecutando Fase ${phase}...`, description: 'El agente analiza con datos reales y web search. 2-5 minutos.' });
    try {
      await axios.post(`${BACKEND_URL}/api/audit/run-phase`, {
        phase,
        extra_context: extraContext || null,
      });
      toast({ title: `Fase ${phase} completada`, description: 'Informe guardado.' });
      loadReports();
    } catch (e) {
      toast({ title: `Error en Fase ${phase}`, description: e.response?.data?.detail || 'Error desconocido', variant: 'destructive' });
    }
    setRunning(null);
  };

  const runSynthesis = async () => {
    setRunning(99);
    toast({ title: 'Montando el Plan Maestro...', description: 'El Coordinador ensambla las 9 fases en un informe único. Unos minutos.' });
    try {
      await axios.post(`${BACKEND_URL}/api/audit/run-synthesis`);
      toast({ title: 'Plan Maestro listo', description: 'El Brief se ha actualizado y el informe completo está montado.' });
      loadReports();
    } catch (e) {
      toast({ title: 'Error en el Plan Maestro', description: e.response?.data?.detail || 'Error desconocido', variant: 'destructive' });
    }
    setRunning(null);
  };

  const getLatestReport = (phase) => {
    return reports
      .filter(r => Number(r.phase) === Number(phase))
      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))[0];
  };

  const synthesis = reports
    .filter(r => Number(r.phase) === 99)
    .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))[0];

  const completedCount = PHASES.filter(p => getLatestReport(p.phase)).length;

  return (
    <div className="space-y-4">
      {/* Plan Maestro / Coordinador */}
      <Card className="border-2 border-slate-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" /> Plan Maestro — Coordinador Central
          </CardTitle>
          <CardDescription>
            Ejecútalo al final, cuando hayas completado las 9 fases ({completedCount}/9 hechas).
            El Coordinador ensambla las 9 fases en un único informe con los 19 bloques (sin recortes) y actualiza el Brief.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={runSynthesis}
            disabled={running !== null}
            className="bg-slate-900 hover:bg-slate-800 text-white w-full"
          >
            {running === 99 ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Montando el informe...</>
            ) : (
              <><PlayCircle className="mr-2 h-4 w-4" /> Generar Plan Maestro</>
            )}
          </Button>

          {synthesis && (
            <div className="mt-4">
              <div
                className="flex items-center justify-between cursor-pointer p-3 bg-slate-50 rounded border"
                onClick={() => setExpanded(expanded === 99 ? null : 99)}
              >
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-900 text-white">Plan Maestro</Badge>
                  <span className="text-sm text-slate-500">
                    {new Date(synthesis.timestamp).toLocaleString('es-ES')}
                  </span>
                </div>
                {expanded === 99 ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              {expanded === 99 && (
                <>
                  <div className="flex justify-end mt-2 mb-1">
                    <Button variant="outline" size="sm" onClick={() => downloadPDF('Plan Maestro — Informe 19 Bloques', synthesis.analysis)}>
                      <Download className="h-4 w-4 mr-1" /> Descargar PDF
                    </Button>
                  </div>
                  <div className="mt-1 text-sm leading-relaxed bg-white p-4 rounded border max-h-[600px] overflow-auto"
                    dangerouslySetInnerHTML={{ __html: renderAnalysis(synthesis.analysis) }}
                  />
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contexto adicional */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contexto adicional (opcional)</CardTitle>
          <CardDescription>
            Si tienes información extra para esta sesión (novedades, cambios recientes), pégala aquí.
            Escribe REFRESCAR_CONECTORES si quieres forzar que vuelva a leer los conectores ahora mismo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <textarea
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 resize-y"
            rows={3}
            placeholder="Ej: Esta semana hice una campaña nueva con este copy... / Las ventas bajaron un 20%..."
            value={extraContext}
            onChange={(e) => setExtraContext(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Las 9 fases */}
      <div className="grid grid-cols-1 gap-3">
        {PHASES.map(({ phase, name, description }) => {
          const report = getLatestReport(phase);
          return (
            <Card key={phase}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">Fase {phase}</Badge>
                      <span className="font-semibold text-slate-900">{name}</span>
                      {report && <Badge className="bg-green-500 text-white text-xs">Completada</Badge>}
                    </div>
                    <p className="text-sm text-slate-500">{description}</p>
                    {report && (
                      <p className="text-xs text-slate-400 mt-1">
                        Último análisis: {new Date(report.timestamp).toLocaleString('es-ES')}
                        {report.web_search_used && ' · Web search ✓'}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {report && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPDF(`Fase ${phase} — ${name}`, report.analysis)}
                          title="Descargar PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpanded(expanded === phase ? null : phase)}
                        >
                          {expanded === phase ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          Ver
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      onClick={() => runPhase(phase)}
                      disabled={running !== null}
                      className={report ? "bg-slate-600 hover:bg-slate-700" : "bg-blue-600 hover:bg-blue-700"}
                    >
                      {running === phase ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><PlayCircle className="h-4 w-4 mr-1" />{report ? 'Re-ejecutar' : 'Ejecutar'}</>
                      )}
                    </Button>
                  </div>
                </div>

                {expanded === phase && report && (
                  <div className="mt-4 pt-3 border-t">
                    <div className="text-sm leading-relaxed bg-slate-50 p-4 rounded border max-h-[500px] overflow-auto"
                      dangerouslySetInnerHTML={{ __html: renderAnalysis(report.analysis) }}
                    />
                    {report.citations && report.citations.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-400 mb-1">Fuentes web:</p>
                        {report.citations.slice(0, 5).map((c, i) => (
                          <a key={i} href={c.url} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-500 hover:underline block truncate">
                            {c.title || c.url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AuditPage;
